import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { Shield, KeyRound, Loader2, X, AlertCircle, Mail, CheckCircle2 } from 'lucide-react';
import { safeFetch } from '../../lib/api';
import { useModalLayer } from '../../lib/modalStack';

interface DoubleConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  targetUid: string;
  newRole: string;
  entityType: 'workspace' | 'employer' | 'referrer' | 'sponsor';
  entityId?: string;
}

export default function DoubleConfirmationModal({
  isOpen,
  onClose,
  onSuccess,
  targetUid,
  newRole,
  entityType,
  entityId,
}: DoubleConfirmationModalProps) {
  const z = useModalLayer(isOpen);
  const [step, setStep] = useState<'password' | 'otp'>('password');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [maskedEmail, setMaskedEmail] = useState('');

  if (!isOpen) return null;

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password) {
      setError('Password is required');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await safeFetch('/api/workspaces/members/role-secure-send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          targetUid,
          newRole,
          entityType,
          entityId,
          password,
          firebaseApiKey: import.meta.env.VITE_FIREBASE_API_KEY || '',
        }),
      });
      setMaskedEmail(res.maskedEmail || 'the Master organization contact email');
      setStep('otp');
    } catch (err: any) {
      setError(err.message || 'Verification failed. Please check your password.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!code || code.trim().length !== 5) {
      setError('Please enter a valid 5-digit verification code.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await safeFetch('/api/workspaces/members/role-secure-verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          targetUid,
          newRole,
          entityType,
          entityId,
          code: code.trim(),
        }),
      });
      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Invalid or expired verification code.');
    } finally {
      setLoading(false);
    }
  };

  const roleLabel =
    entityType === 'workspace'
      ? ({ admin: 'Admin', manager: 'Manager', member: 'Team Member', viewer: 'Viewer' } as Record<string, string>)[newRole] || newRole
      : newRole === 'admin' ? 'Admin' : newRole === 'super' ? 'Super Agent' : newRole === 'enhanced' ? 'Enhanced Referrer' : newRole;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4" style={{ zIndex: z }} onClick={onClose}>
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden animate-fade-in" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-slate-50/50">
          <div className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-brand" />
            <span className="text-xs font-black text-slate-800 uppercase tracking-widest">Double Confirmation Gated</span>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-700 transition cursor-pointer">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="mb-4">
            <h3 className="text-sm font-black text-slate-900 leading-snug">
              Confirm Role Change to <span className="text-brand capitalize font-black">{roleLabel}</span>
            </h3>
            <p className="text-[11px] text-slate-400 mt-1 leading-snug">
              This is a security-sensitive operations action. You must verify your credentials and confirm with the Master administrator code.
            </p>
          </div>

          {error && (
            <div className="flex items-start gap-2 bg-rose-50 border border-rose-100 rounded-xl p-3 text-xs text-rose-600 mb-4 animate-shake">
              <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
              <p className="font-medium leading-normal">{error}</p>
            </div>
          )}

          {step === 'password' ? (
            <form onSubmit={handleSendCode} className="space-y-4">
              <div>
                <label className="block text-[10px] font-black uppercase tracking-wider text-slate-400 mb-1.5">Enter Your Current Password</label>
                <div className="relative">
                  <KeyRound className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                  <input
                    type="password"
                    required
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full pl-10 pr-4 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand/20 bg-white"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading || !password}
                className="w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-xl btn-primary text-white text-xs font-black cursor-pointer disabled:opacity-50"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Shield className="h-4 w-4" />}
                {loading ? 'Verifying Password...' : 'Verify Password & Request OTP'}
              </button>
            </form>
          ) : (
            <form onSubmit={handleVerifyCode} className="space-y-4">
              <div className="bg-emerald-50/70 border border-emerald-100 rounded-xl p-3 text-xs text-emerald-800 flex items-start gap-2">
                <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600 mt-0.5" />
                <div>
                  <p className="font-bold">Identity Verified</p>
                  <p className="text-[10px] text-emerald-600 leading-snug mt-0.5">
                    We sent a 5-digit verification code to the Master contact: <b className="font-mono">{maskedEmail}</b>. Please check your inbox (and spam folder).
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-[10px] font-black uppercase tracking-wider text-slate-400 mb-1.5">5-Digit Verification Code</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                  <input
                    type="text"
                    required
                    maxLength={5}
                    value={code}
                    onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
                    placeholder="12345"
                    className="w-full pl-10 pr-4 py-2 text-sm border border-slate-200 rounded-xl tracking-[0.5em] font-mono text-center focus:outline-none focus:ring-2 focus:ring-brand/20 bg-white"
                  />
                </div>
              </div>

              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setStep('password')}
                  className="flex-1 py-2.5 border border-slate-200 rounded-xl text-slate-600 text-xs font-bold hover:bg-slate-50 cursor-pointer"
                >
                  Back
                </button>
                <button
                  type="submit"
                  disabled={loading || code.length !== 5}
                  className="flex-1 inline-flex items-center justify-center gap-2 py-2.5 rounded-xl btn-primary text-white text-xs font-black cursor-pointer disabled:opacity-50"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Confirm Role Change'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
